import { Company, User, Record, Currency, Attachment, ComplianceCountry } from "./general";

/**
 * Client-side mirror of backend PayrollComplianceConstants.ProbationMaxDaysFor /
 * ProbationLegalReferenceFor (UAE 183 — FDL 33/2021 Art. 9; KSA 180 — Labor Law Art. 53-54).
 * Kept on the client ONLY because the profile form must validate an UNSAVED probation date
 * before any backend round-trip. Compliance-list rows must instead use the per-row
 * `maxAllowedDays` / `legalReference` the backend already returns — do not re-derive there.
 * If the statutory caps change, update this and the backend constants together.
 */
export const probationMaxDaysFor = (country: ComplianceCountry): number | null =>
  country === 'KSA' ? 180 : country === 'UAE' ? 183 : null;

export const probationLegalReferenceFor = (country: ComplianceCountry): string =>
  country === 'KSA' ? 'KSA Labor Law Art. 53-54' : 'FDL 33/2021 Art. 9';

/**
 * Client-side mirror of backend PayrollComplianceConstants.GratuityLegalReferenceFor.
 * EOS gratuity citation by country (UAE — FDL 33/2021 Art. 51; KSA — Labor Law Art. 84-85).
 * Keep in sync with the backend constant if the statutory citations change.
 */
export const gratuityLegalReferenceFor = (country: ComplianceCountry): string =>
  country === 'KSA' ? 'KSA Labor Law Art. 84-85' : 'FDL 33/2021 Art. 51';

/**
 * Client-side mirror of backend SettlementComplianceRules.For (Data/Payroll/SettlementComplianceRules.cs)
 * + the PayrollComplianceConstants notice accessors. Drives the Final Settlement form's notice-period
 * bounds so the client can validate an UNSAVED settlement before the backend round-trip. `initiator`
 * comes from {@link classifyInitiator}; `isFixedTerm` mirrors the backend's test — fixed-term =
 * contains "limited" AND NOT "unlimited" (so "Unlimited" is not misclassified). Keep in sync with the backend record if the
 * statutory numbers/citations change.
 */
export interface SettlementNoticeRules {
  noticeApplies: boolean;
  floorDays: number;
  ceilingDays: number;
  legalReference: string;
}

export const settlementNoticeRulesFor = (
  country: ComplianceCountry,
  initiator: DepartureInitiator,
  isFixedTerm: boolean,
): SettlementNoticeRules => {
  switch (country) {
    // UAE (FDL 33/2021) — notice skipped for Neutral departures; 30–90 window.
    case 'UAE':
      return { noticeApplies: initiator !== 'Neutral', floorDays: 30, ceilingDays: 90, legalReference: 'FDL 33/2021 Art. 43' };
    // KSA (Labor Law Art. 75-76) — fixed-term carries no statutory notice; indefinite: employer ≥60 / worker ≥30.
    case 'KSA':
      return {
        noticeApplies: !isFixedTerm && initiator !== 'Neutral',
        floorDays: initiator === 'Employer' ? 60 : 30,
        ceilingDays: 365,
        legalReference: 'KSA Labor Law Art. 75-76',
      };
    // Unmodelled country — permissive: no statutory floor imposed.
    default:
      return { noticeApplies: false, floorDays: 0, ceilingDays: Number.MAX_SAFE_INTEGER, legalReference: 'Company policy' };
  }
};

/** Settlement deduction-cap citation, by country. Mirrors backend DeductionCapLegalReferenceFor (UAE Art. 25 / KSA Art. 93). */
export const settlementDeductionRefFor = (country: ComplianceCountry): string =>
  country === 'KSA' ? 'KSA Labor Law Art. 93' : country === 'UAE' ? 'FDL 33/2021 Art. 25' : 'Company policy';

/**
 * Employer-loan repayment sub-cap fraction (of the settlement gross), by country. Mirrors backend
 * SettlementComplianceRules.EmployerLoanCapFraction: KSA Art. 92 caps it at 10%; other countries model
 * no sub-cap (null). Surfaced as the KSA-only employer-loan input's client-side validation.
 */
export const settlementEmployerLoanCapFractionFor = (country: ComplianceCountry): number | null =>
  country === 'KSA' ? 0.10 : null;

/** Citation for the employer-loan repayment sub-cap (KSA Labor Law Art. 92). */
export const SETTLEMENT_EMPLOYER_LOAN_REFERENCE = 'KSA Labor Law Art. 92';

// ..\mowazaf\Data\Payroll\Enities\CompanyBenefitType.cs
export interface CompanyBenefitType extends Record {
    id: number;
    companyId: number;
    company?: Company;
    name: string;
    description?: string;
    visibleToEmployees: boolean;
    price?: number;
    cost?: number;
    currencyId: number;
    currency?: Currency;
}

// ..\mowazaf\Data\Payroll\Enities\CompanyClaimType.cs
export interface CompanyClaimType extends Record {
    id: number;
    name: string;
    description?: string;
    companyId: number;
    company?: Company;
    maxAmountPerClaim?: number | null;
    monthlyLimitPerEmployee?: number | null;
    annualLimitPerEmployee?: number | null;
    requiresReceiptAbove?: number | null;
}

// ..\mowazaf\Data\Payroll\Enities\CompanyGrossPay.cs
export interface CompanyGrossPay extends Record {
    id: number;
    name: string;
    description?: string;
    companyId: number;
    company?: Company;
    amountCalculationType: AmountCalculationType;
    type: GrossPayType;
    taxability: Taxability;
    proofRequired: boolean;
    amount?: number;
    formulaPercentage?: number;
    companyGrossPayForFormulaId?: number;
    companyGrossPayForFormula?: CompanyGrossPay;
    /** @deprecated MERGED INTO {@link isBasicSalary} — see `isBasicSalaryComponent`. */
    isPrimary?: boolean;
    includedInWps: boolean;
    isBasicSalary?: boolean;
    mudadCategory?: MudadAllowanceCategory | null;
}

/**
 * 🔴 THE ONE BASIC-SALARY FLAG (owner decision, 2026-08-19).
 *
 * `IsPrimary` ("the component carrying each employee's own amount") and `IsBasicSalary` ("the
 * statutory basic wage") were two flags for one real-world thing. The owner settled it: a housing
 * or fixed per-company line is never the legal basic component, so the per-employee salary
 * component IS the basic salary. One flag, backfilled once by migration.
 *
 * The READER below still tolerates `isPrimary` on purpose: `CompanyGrossPayView` keeps it as a
 * READ-ONLY computed alias (`=> IsBasicSalary`), so a payload carrying either shape resolves the same.
 * A read alias cannot re-create the CO-163 defect; only a WRITE channel could, and there is no
 * write-triggered flip, the value is whatever the operator explicitly ticked.
 */
export const isBasicSalaryComponent = (
  c: { isBasicSalary?: boolean | null; isPrimary?: boolean | null } | null | undefined,
): boolean => (c?.isBasicSalary ?? c?.isPrimary ?? false) === true;

/**
 * Writes the single flag onto an input. `isBasicSalary` is now the ONLY field sent: the server's
 * deprecated `IsPrimary` wire alias was removed on 2026-08-19, and while the dual-write was harmless
 * it kept a second channel alive for one flag — which is the shape of the CO-163 defect.
 *
 * 🔴 Never call this with a derived or implicit value. It must carry exactly what the operator ticked.
 * CO-163 was caused by a flag inferred from other state: a screen echoed a value back from state loaded
 * at page open, and saving something unrelated silently re-asserted the statutory flag.
 */
export const withBasicSalaryFlag = <T extends { isBasicSalary?: boolean }>(
  input: T,
  checked: boolean,
): T => ({ ...input, isBasicSalary: checked });

// ..\mowazaf\Data\Payroll\Enities\CompanyGrossPay.cs
export enum AmountCalculationType {
    EmployeeSpecificAmount = 'EmployeeSpecificAmount',
    FormulaBasedAmount = 'FormulaBasedAmount',
    FixedAmount = 'FixedAmount',
}

// ..\mowazaf\Data\Payroll\Enities\CompanyGrossPay.cs
export enum GrossPayType {
    Fixed = 'Fixed',
    Allowance = 'Allowance',
}

// ..\mowazaf\Data\Payroll\Enities\CompanyGrossPay.cs
export enum Taxability {
    Taxable = 'Taxable',
    Exempted = 'Exempted',
}

// ..\mowazaf\Data\Payroll\Enities\CompanyVariablePayType.cs
export interface CompanyVariablePayType extends Record {
    id: number;
    name: string;
    description?: string;
    companyId: number;
    company?: Company;
}

// ..\mowazaf\Data\Payroll\Enities\EndOfService.cs
export interface EndOfService extends Record {
    id: number;
    type: string;
    companyId: number;
    company: Company;
    description?: string;
}

// ..\mowazaf\Data\Payroll\Enities\EndOfServiceDetails.cs
export interface EndOfServiceDetails extends Record {
    id: number;
    endOfServiceId: number;
    endOfService: EndOfService;
    years: number;
    salaryComponentId: number;
    salaryComponent: CompanyGrossPay;
    multiplier: number;
}

// ..\mowazaf\Data\Payroll\Enities\RequestType.cs
export enum RequestType {
    PaySlip = 'PaySlip',
    PayCertificate = 'PayCertificate',
    Claim = 'Claim',
}

// ..\mowazaf\Data\Payroll\Enities\UserAction.cs
export interface UserAction extends Record {
    id: number;
    userId: string;
    user: User;
    requestType: RequestType;
}

// ..\mowazaf\Data\Payroll\Enities\UserAdjustment.cs
export interface UserAdjustment extends Record {
    id: number;
    userId: string;
    user?: User;
    userAdjustmentType: UserAdjustmentType;
    /** FDL 33/2021 Art. 25 — CourtOrder is exempt from the 50% monthly cap */
    deductionType: DeductionType;
    remarks?: string;
    amount: string;
    effectiveDate: Date;
    dateOfApprovalOrRejection?: Date;
    paymentDate?: Date;
    status: UserAdjustmentStatus;
    currencyId: number;
    currency?: Currency;
    userMonthlyPayrollId?: number;
    userMonthlyPayroll?: UserMonthlyPayroll;
}

// ..\mowazaf\Data\Payroll\Enities\UserAdjustment.cs
export enum UserAdjustmentType {
    Addition = 'Addition',
    Deduction = 'Deduction',
}

// ..\mowazaf\Data\Payroll\Enities\DeductionType.cs
// FDL 33/2021 Art. 25 — CourtOrder is exempt from the 50% monthly wage cap; all others count toward it.
export enum DeductionType {
    Standard = 'Standard',
    CourtOrder = 'CourtOrder',
    Disciplinary = 'Disciplinary',
    Loan = 'Loan',
    Other = 'Other',
}

// ..\mowazaf\Data\Payroll\Enities\UserAdjustment.cs
export enum UserAdjustmentStatus {
    Approved = 'Approved',
    Rejected = 'Rejected',
    Pending = 'Pending',
    PendingPayment = 'PendingPayment',
    Paid = 'Paid',
}

// ..\mowazaf\Data\Payroll\Enities\UserBenefit.cs
export interface UserBenefit extends Record {
    id: number;
    userId: string;
    user?: User;
    companyBenefitTypeId: number;
    companyBenefitType?: CompanyBenefitType;
    effectiveDate: Date;
    expiryDate?: Date;
}

// ..\mowazaf\Data\Payroll\Enities\UserClaim.cs
export interface UserClaim extends Record {
    id: number;
    userId: string;
    user?: User;
    companyClaimTypeId: number;
    companyClaimType?: CompanyClaimType;
    amount: string;
    comments?: string;
    status: UserClaimStatus;
    managerId?: string;
    manager?: User;
    managerComment?: string;
    requestDate: Date;
    dateOfApprovalOrRejection?: Date;
    paymentDate?: Date;
    currencyId: number;
    currency?: Currency;
    userClaimAttachments?: UserClaimAttachment[];
    userMonthlyPayrollId?: number;
    userMonthlyPayroll?: UserMonthlyPayroll;
}

// ..\mowazaf\Data\Payroll\Enities\UserClaim.cs
export enum UserClaimStatus {
    Approved = 'Approved',
    Rejected = 'Rejected',
    Pending = 'Pending',
    PendingPayment = 'PendingPayment',
    Paid = 'Paid',
}

// ..\mowazaf\Data\Payroll\Enities\UserClaimAttachment.cs
export interface UserClaimAttachment extends Attachment {
    userClaimId: number;
    userClaim?: UserClaim;
}

// ..\mowazaf\Data\Payroll\Enities\UserEndOfService.cs
export interface UserEndOfService extends Record {
    id: number;
    userId: string;
    user: User;
    amount: number;
    years: number;
    departureReason: string;
    contractType: string;
}

// ..\mowazaf\Data\Payroll\Enities\UserExpense.cs
export interface UserExpense extends Record {
    id: number;
    userId?: string;
    user?: User;
    expenseId?: number;
    expense: CompanyClaimType;
}

// ..\mowazaf\Data\Payroll\Enities\UserFinalSettlement.cs
export interface UserFinalSettlement extends Record {
    id: number;
    userId: string;
    noticePeriod: number;
    countOnlyWorkingDays: boolean;
    finalSettelmentAmount: number;
    comments?: string;
}

// ..\mowazaf\Data\Payroll\Enities\UserGrossPay.cs
export interface UserGrossPay extends Record {
    id: number;
    userId: string;
    user?: User;
    companyGrossPayId?: number;
    companyGrossPay?: CompanyGrossPay;
    remarks?: string;
    amount: string;
    effectiveStartDate: Date;
    effectiveStopDate?: Date;
    currencyId: number;
    currency?: Currency;
    userGrossPayForFormulaId?: number;
    userGrossPayForFormula?: UserGrossPay;
}

// ..\mowazaf\Data\Payroll\Enities\UserMonthlyPayroll.cs
export interface UserMonthlyPayroll extends Record {
    id: number;
    userId: string;
    user?: User;
    grossSalary?: string;
    overtime?: string;
    variablePay?: string;
    adjustments?: string;
    deductions?: string;
    benefitDeductions?: string;
    totalNetPay?: string;
    date: Date;
    status: boolean;
    currencyId: number;
    currency?: Currency;
    userClaims?: UserClaim[];
    userAdjustments?: UserAdjustment[];
    userVariablePays?: UserVariablePay[];
}

// ..\mowazaf\Data\Payroll\Enities\UserVariablePay.cs
export interface UserVariablePay extends Record {
    id: number;
    userId: string;
    user?: User;
    companyVariablePayTypeId: number;
    companyVariablePayType?: CompanyVariablePayType;
    remarks?: string;
    amount: string;
    effectiveDate: Date;
    dateOfApprovalOrRejection?: Date;
    paymentDate?: Date;
    status: UserVariablePayStatus;
    currencyId: number;
    currency?: Currency;
    userMonthlyPayrollId?: number;
    userMonthlyPayroll?: UserMonthlyPayroll;
}

// ..\mowazaf\Data\Payroll\Enities\UserVariablePay.cs
export enum UserVariablePayStatus {
    Approved = 'Approved',
    Rejected = 'Rejected',
    Pending = 'Pending',
    PendingPayment = 'PendingPayment',
    Paid = 'Paid',
}

// ..\mowazaf\Data\Payroll\InputModels\CalculateEndOfService.cs
export interface CalculateEndOfServiceInput {
    endOfServiceId: number;
    userId: string;
    hireDate: Date;
    departureReason: string;
    contractType: string;
    amount?: number;
    years?: number;
}

// ..\mowazaf\Data\Payroll\InputModels\CompanyBenefitTypeInput.cs
export interface CompanyBenefitTypeInput {
    id: number;
    name?: string;
    description?: string;
    visibleToEmployees?: boolean;
    price?: number;
    cost?: number;
}

// ..\mowazaf\Data\Payroll\InputModels\CompanyClaimTypeInput.cs
export interface CompanyClaimTypeInput {
    name: string;
    description?: string;
    maxAmountPerClaim?: number | null;
    monthlyLimitPerEmployee?: number | null;
    annualLimitPerEmployee?: number | null;
    requiresReceiptAbove?: number | null;
}

// ..\mowazaf\Data\Payroll\InputModels\CompanyGrossPayInput.cs
export interface CompanyGrossPayInput {
    id: number;
    name?: string;
    description?: string;
    amountCalculationType: AmountCalculationType;
    type: GrossPayType;
    taxability: Taxability;
    proofRequired: boolean;
    amount?: number;
    formulaPercentage?: number;
    companyGrossPayForFormulaId?: number;
    /** @deprecated MERGED INTO {@link isBasicSalary} — see `isBasicSalaryComponent`. */
    isPrimary?: boolean;
    includedInWps: boolean;
    isBasicSalary?: boolean;
    mudadCategory?: MudadAllowanceCategory | null;
}

// ..\mowazaf\Data\Payroll\InputModels\CompanyVariablePayTypeInput.cs
export interface CompanyVariablePayTypeInput {
    name: string;
    description?: string;
}

// ..\mowazaf\Data\Payroll\InputModels\EndOfServiceDetailsInput.cs
export interface EndOfServiceDetailsInput {
    typeId: number;
    years: number;
    salaryComponentId: number;
    multiplier: number;
}

// ..\mowazaf\Data\Payroll\InputModels\EndOfServiceTypeInput.cs
export interface EndOfServiceTypeInput {
    type: string;
    description: string;
}

// ..\mowazaf\Data\Payroll\InputModels\FinalSettlementInput.cs
export interface FinalSettelmentInput {
    userId: string;
    noticePeriod: number;
    countOnlyWorkingDays: boolean;
    amount?: number;
}

// ..\mowazaf\Data\Payroll\InputModels\UserAdjusmentInput.cs
export interface UserAdjustmentInput {
    id: number;
    userId?: string;
    userAdjustmentType?: UserAdjustmentType;
    /** FDL 33/2021 Art. 25 — only meaningful when userAdjustmentType === 'Deduction' */
    deductionType?: DeductionType;
    remarks?: string;
    amount?: number;
    effectiveDate?: Date;
    status?: UserAdjustmentStatus;
}

// ..\mowazaf\Data\Payroll\InputModels\UserBenefitInput.cs
export interface UserBenefitInput {
    id?: number;
    userId?: string;
    companyBenefitTypeId?: number;
    effectiveDate?: Date;
    expiryDate?: Date;
}

// ..\mowazaf\Data\Payroll\InputModels\UserClaimInput.cs
export interface UserClaimInput {
    id: number;
    amount?: string;
    companyClaimTypeId?: number;
    status?: UserClaimStatus;
    comments?: string;
    managerComment?: string;
}

// ..\mowazaf\Data\Payroll\InputModels\UserGrossPayBatchInput.cs
export interface UserGrossPayBatchInput {
    userIds: string[];
    companyGrossPayId: number;
    remarks?: string;
    amount?: number;
    effectiveStartDate?: Date;
    effectiveStopDate?: Date;
}

// ..\mowazaf\Data\Payroll\InputModels\UserGrossPayInput.cs
export interface UserGrossPayInput {
    id: number;
    userId: string;
    companyGrossPayId: number;
    remarks?: string;
    amount?: number;
    effectiveStartDate: string;
    effectiveStopDate?: string;
}

// ..\mowazaf\Data\Payroll\InputModels\UserVariablePayInput.cs
export interface UserVariablePayInput {
    id: number;
    userId?: string;
    companyVariablePayTypeId?: number;
    remarks?: string;
    amount?: number;
    effectiveDate?: Date;
    status?: UserVariablePayStatus;
}

// ..\mowazaf\Data\Payroll\RequestInputModels\MonthlyPayrollInput.cs
export interface MonthlyPayrollInput {
    month: number;
    year: number;
    userIds?: string[];
    payslipIds?: number[];
    currencyId: number;
}

// ..\mowazaf\Data\Payroll\RequestInputModels\PayrollPreflightInput.cs
export interface PayrollPreflightInput {
    year: number;
    month: number;
    userIds?: string[];
}

// ..\mowazaf\Data\Payroll\ViewModels\PayrollPreflightView.cs
export interface PayrollPreflightUserView {
    userId: string;
    fullName: string;
}

export interface PayrollPreflightApprovalView {
    userId: string;
    fullName: string;
    pendingClaims: number;
    pendingAdjustments: number;
    pendingVariablePays: number;
}

/**
 * Hourly pay §5.4 (D6) — hourly shifts still Pending for the month being generated, per employee.
 * A WARNING, never a blocker: they are paid in the next run once approved.
 * ⚠️ Field names are the web lane's assumption; docs/hourly-pay-spec.md §5.4 names no shape. The
 * UI renders the section only when the server sends it.
 */
export interface PayrollPreflightHourlyPendingView {
    userId: string;
    fullName: string;
    pendingCount: number;
    pendingMinutes: number;
}

export interface PayrollPreflightView {
    missingIban: PayrollPreflightUserView[];
    missingLabourCard: PayrollPreflightUserView[];
    alreadyPaid: PayrollPreflightUserView[];
    pendingApprovals: PayrollPreflightApprovalView[];
    missingIbanCount: number;
    missingLabourCardCount: number;
    alreadyPaidCount: number;
    pendingApprovalsCount: number;
    hasBlockers: boolean;
    /** §5.4 — absent on servers without hourly pay. */
    hourlyPendingShifts?: PayrollPreflightHourlyPendingView[];
    hourlyPendingShiftsCount?: number;
    hourlyPendingMinutes?: number;
    /** §5.3 carry-forward (D10) — absent on older servers. `amount` is signed like the lines (negative). */
    hourlyCarriedForward?: PayrollPreflightHourlyCarryView[];
    hourlyCarriedForwardTotal?: number;
    /** §8 — per HOURLY employee in the selection, the pension / GOSI base this run uses (their approved
     *  hourly earnings). Absent when the selection has no hourly employee, and on older servers. */
    hourlyStatutoryBase?: PayrollPreflightHourlyStatutoryView[];
    /** CO-657 — employees the run will SKIP because their last working day is before the period
     *  starts. Absent on older servers. Warning, never a blocker. */
    exitedBeforePeriod?: PayrollPreflightUserView[];
    exitedBeforePeriodCount?: number;
    /** CO-657 — payslips already on file for a period AFTER the employee's exit, flagged when the exit
     *  was processed. Never deleted; this list is how HR finds them. Absent on older servers. */
    postExitPayslips?: PayrollPreflightPostExitPayslipView[];
    postExitPayslipsCount?: number;
}

/** CO-657 — one already-generated payslip belonging to a period after the employee's exit date. */
export interface PayrollPreflightPostExitPayslipView {
    userId: string;
    fullName: string;
    /** `yyyy-MM`. */
    period: string;
    /** True when that payslip has already been marked PAID — the case that needs HR first. */
    paid: boolean;
}

export interface PayrollPreflightHourlyCarryView {
    userId: string;
    fullName: string;
    amount: number;
}

export interface PayrollPreflightHourlyStatutoryView {
    userId: string;
    fullName: string;
    /** §8 statutory basic = the month's approved hourly earnings (≥ 0). */
    basicWage: number;
    /** Employee statutory deduction computed on it (GPSSA employee share, or GOSI ANI + SANED). */
    employeeDeduction: number;
}

// ..\mowazaf\Data\Payroll\ViewModels\CompanyBenefitTypeView.cs
export interface CompanyBenefitTypeView {
    id: number;
    name: string;
    description?: string;
    visibleToEmployees: boolean;
    price?: number;
    cost?: number;
    currencyId: number;
    currency?: Currency;
    numberOfBeneficiaries: number;
    totalCost: number;
    totalPrice: number;
}

// ..\mowazaf\Data\Payroll\ViewModels\CompanyClaimTypeView.cs
export interface CompanyClaimTypeView {
    id: number;
    name: string;
    description?: string;
    companyId: number;
    company?: Company;
    numberOfBeneficiaries: number;
    totalCost: number;
    departmentName?: string;
    userName?: string;
    usersNames?: string[];
    maxAmountPerClaim?: number | null;
    monthlyLimitPerEmployee?: number | null;
    annualLimitPerEmployee?: number | null;
    requiresReceiptAbove?: number | null;
}

// ..\mowazaf\Data\Payroll\ViewModels\CompanyGrossPayView.cs
export interface CompanyGrossPayView {
    id: number;
    name: string;
    description?: string;
    companyId: number;
    amountCalculationType: AmountCalculationType;
    type: GrossPayType;
    taxability: Taxability;
    proofRequired: boolean;
    amount?: number;
    formulaPercentage?: number;
    companyGrossPayForFormulaId?: number;
    companyGrossPayForFormula?: CompanyGrossPayView;
    /** @deprecated MERGED INTO {@link isBasicSalary} — see `isBasicSalaryComponent`. */
    isPrimary?: boolean;
    includedInWps: boolean;
    isBasicSalary?: boolean;
    mudadCategory?: MudadAllowanceCategory | null;
    /**
     * CO-163 — populated ONLY on an Add/Edit response, never on a read. Names of the components
     * that lost the statutory basic-wage flag because this save took it. The UI used to derive
     * this from its own pre-save list, which is stale and says nothing about what the server did.
     */
    demotedBasicSalaryComponents?: string[] | null;
    /** CO-163 — write response: this save cleared the flag and left the company with none at all. */
    clearedLastBasicSalaryComponent?: boolean;
    /** CO-163 — write response: how many active components carry the flag after the save. */
    basicSalaryComponentCount?: number;
}

// ..\mowazaf\Data\Payroll\ViewModels\CompanyVariablePayTypeView.cs
export interface CompanyVariablePayTypeView {
    id: number;
    name: string;
    description?: string;
    companyId: number;
    company?: Company;
}

// ..\mowazaf\Data\Payroll\ViewModels\EndOfServiceDetailsView.cs
export interface EndOfServiceDetailsView {
    id: number;
    endOfServiceId: number;
    endOfService: EndOfService;
    years: number;
    salaryComponentId: number;
    salaryComponent: CompanyGrossPay;
    multiplier: number;
    amount: number;
}

// ..\mowazaf\Data\Payroll\ViewModels\EndOfServiceTypeView.cs
export interface EndOfServiceTypeView {
    id: number;
    type: string;
    companyId: number;
    description: string;
}

// ..\mowazaf\Data\Payroll\ViewModels\FinalSettelmentView.cs
export interface FinalSettelmentView {
    id: number;
    userId: string;
    noticePeriod: number;
    countOnlyWorkingDays: boolean;
    finalSettelmentAmount: number;
}

// ..\mowazaf\Data\Payroll\ViewModels\MonthlyPaySlipTempate.cs
export interface MonthlyPayrollTemplate {
    firstname: string;
    amount: number;
}

// ..\mowazaf\Data\Payroll\ViewModels\MonthlyPayrollView.cs
export interface MonthlyPayrollView {
    id: number;
    userId: string;
    user: User;
    grossSalary: number;
    overtime: number;
    variablePay: number;
    adjustments: number;
    deductions: number;
    totalNetPay: number;
    month: string;
    year: string;
    status: boolean;
    // FDL 33/2021 Art. 19/20/21 — monetized OT + hour split
    overtimePay?: number;
    regularOvertimeHours?: number;
    premiumOvertimeHours?: number;
    onProbation?: boolean;
    probationDays?: number | null;
    probationExceeded?: boolean;
    /** True when the requested currency has NO configured FX rate from this payslip's currency: every money field is a 1:1 passthrough, not a conversion. */
    currencyRateUnavailable?: boolean;
    /** CO-610 — the payslip's own currency code, sent only when `currencyRateUnavailable` is true. */
    unconvertedCurrencyCode?: string | null;
}

// ..\mowazaf\Data\Payroll\ViewModels\UserActionView.cs
export interface UserActionView {
    id: number;
    userId: string;
    user: User;
    fullName: string;
    requestType: RequestType;
    dateRequested: Date;
}

// ..\mowazaf\Data\Payroll\ViewModels\UserAdjustmentView.cs
export interface UserAdjustmentView {
    id: number;
    userId: string;
    user?: User;
    userAdjustmentType: UserAdjustmentType;
    /** FDL 33/2021 Art. 25 — CourtOrder is exempt from the 50% monthly cap */
    deductionType: DeductionType;
    remarks?: string;
    amount: number;
    effectiveDate: Date;
    dateOfApprovalOrRejection?: Date;
    paymentDate?: Date;
    status: UserAdjustmentStatus;
    currencyId: number;
    currency?: Currency;
    userMonthlyPayrollId?: number;
    userMonthlyPayroll?: UserMonthlyPayroll;
}

// ..\mowazaf\Data\Payroll\ViewModels\UserBenefitView.cs
export interface UserBenefitView {
    id: number;
    userId: string;
    user?: User;
    companyBenefitTypeId: number;
    companyBenefitType?: CompanyBenefitType;
    effectiveDate: Date;
    expiryDate?: Date;
}

// ..\mowazaf\Data\Payroll\ViewModels\UserClaimAttachmentView.cs
export interface UserClaimAttachmentView {
    id: number;
    claimId: number;
    fileName: string;
    fileExtention: string;
    fileSize: number;
    filePath: string;
    createdOn: Date;
}

// ..\mowazaf\Data\Payroll\ViewModels\UserClaimView.cs
export interface UserClaimView {
    id: number;
    userId: string;
    user?: User;
    companyClaimTypeId: number;
    companyClaimType?: CompanyClaimTypeView;
    amount: string;
    comments?: string;
    status: UserClaimStatus;
    managerId?: string;
    manager?: User;
    managerComment?: string;
    requestDate: Date;
    dateOfApprovalOrRejection?: Date;
    paymentDate?: Date;
    currencyId: number;
    currency?: Currency;
    userClaimAttachments?: UserClaimAttachmentView[];
    userMonthlyPayrollId?: number;
}

// ..\mowazaf\Data\Payroll\ViewModels\UserEndOfServiceView.cs
export interface UserEndOfServiceView {
    id: number;
    userId: string;
    user: User;
    amount: string;
    years: number;
    departureReason: string;
    contractType: string;
}

// ..\mowazaf\Data\Payroll\ViewModels\UserGrossPayView.cs
export interface UserGrossPayView {
    id: number;
    userId: string;
    companyGrossPayId?: number;
    companyGrossPay?: CompanyGrossPay;
    remarks?: string;
    amount: number;
    effectiveStartDate: Date;
    effectiveStopDate?: Date;
    currencyId: number;
    currency?: Currency;
}

// ..\mowazaf\Data\Payroll\ViewModels\UserVariablePayView.cs
export interface UserVariablePayView {
    id: number;
    userId: string;
    user?: User;
    companyVariablePayTypeId: number;
    companyVariablePayType: CompanyVariablePayTypeView;
    remarks?: string;
    amount: number;
    effectiveDate: Date;
    dateOfApprovalOrRejection?: Date;
    paymentDate?: Date;
    status: UserVariablePayStatus;
    currencyId: number;
    currency?: Currency;
    userMonthlyPayrollId?: number;
    userMonthlyPayroll?: UserMonthlyPayroll;
    source: VariablePaySource;
}

export enum VariablePaySource {
    Manual = 'Manual',
    Tournament = 'Tournament',
    Hourly = 'Hourly',
}

// ..\mowazaf\Data\Payroll\Enities\UserPayslip.cs
export interface UserPayslip extends Record {
    id: number;
    userId?: string;
    user?: User;
    payrollDate: Date;
    basicSalary: number;
    variablePay: number;
    benefitDeductions: number;
    deductions: number;
    taxAmount: number;
    netPay: number;
    status?: string;
    includeVariablePay: boolean;
    includeAdjustmentAdditions: boolean;
    includeAdjustmentDeductions: boolean;
    includeDeductions: boolean;
    includeTaxCalculations: boolean;
    generatePdf: boolean;
    sendEmailNotification: boolean;
}

// ..\mowazaf\Data\Payroll\ViewModels\UserPayslipView.cs
export interface UserPayslipView {
    id: number;
    userId: string;
    user?: User;
    payrollDate: Date;
    basicSalary: number;
    variablePay: number;
    /** Total addition-type adjustments (positive value) */
    adjustmentAdditions: number;
    /** Total deduction-type adjustments (positive value) */
    adjustmentDeductions: number;
    /** Net adjustments = adjustmentAdditions - adjustmentDeductions */
    adjustments: number;
    deductions: number;
    taxAmount: number;
    /** FDL 33/2021 Art. 19/20/21 — monetized overtime pay */
    overtimePay: number;
    /** KSA Art. 107 actual wage (basic + fixed allowances) used for the OT breakdown; null for UAE. */
    overtimeActualWage?: number | null;
    /** True basic monthly wage used for the OT hourly base (basic ÷ 208). */
    overtimeBasicWage?: number | null;
    /** OT hours classified regular (UAE ×1.25 tier); KSA folds into the flat total. */
    regularOvertimeHours?: number;
    /** OT hours classified premium (UAE ×1.50 tier); KSA folds into the flat total. */
    premiumOvertimeHours?: number;
    /** GPSSA FL 7/1999 / ADPF AD Law 2/2000 — employee pension contribution */
    pensionDeduction: number;
    /** KSA SI Law 1421/2000 Art. 19 — GOSI ANI employer share (HR/Compliance view only) */
    gosiAniEmployer?: number | null;
    /** KSA SI Law 1421/2000 Art. 19 — GOSI ANI employee share (HR/Compliance view only) */
    gosiAniEmployee?: number | null;
    /** KSA SI Law 1421/2000 — GOSI Occupational Hazards employer share (HR/Compliance view only) */
    gosiOhEmployer?: number | null;
    /** KSA SI Law 1421/2000 — GOSI SANED unemployment insurance employer share (HR/Compliance view only) */
    gosiSanedEmployer?: number | null;
    /** KSA SI Law 1421/2000 — GOSI SANED unemployment insurance employee share (HR/Compliance view only) */
    gosiSanedEmployee?: number | null;
    /** Council of Health Insurance (CCHI) — employee share for non-Saudis. */
    cchiEmployeeShare?: number | null;
    netPay: number;
    status?: string;
    departmentId?: number;
    department?: string;
    hasPdf: boolean;
    includeVariablePay: boolean;
    includeAdjustmentAdditions: boolean;
    includeAdjustmentDeductions: boolean;
    includeDeductions: boolean;
    includeTaxCalculations: boolean;
    generatePdf: boolean;
    sendEmailNotification: boolean;
    createdOn: Date;
    /** Effective currency code for this payslip's employee (e.g. "LBP", "AED") */
    currencyCode?: string;
    /** §8 — only for an hourly employee: the pension / GOSI base, i.e. the month's approved hourly earnings. */
    hourlyStatutoryBase?: number;
}

// ..\mowazaf\Data\Payroll\InputModels\UserPayslipInput.cs
export interface UserPayslipInput {
    id?: number;
    departmentId?: number;
    userId?: string;
    payrollDate?: Date;
    basicSalary?: number;
    variablePay?: number;
    deductions?: number;
    taxAmount?: number;
    netPay?: number;
    status?: string;
    includeVariablePay: boolean;
    includeAdjustmentAdditions: boolean;
    includeAdjustmentDeductions: boolean;
    includeDeductions: boolean;
    includeTaxCalculations: boolean;
    generatePdf: boolean;
    sendEmailNotification: boolean;
}

// ..\mowazaf\Data\Payroll\RequestInputModels\UserPayslipRequestInput.cs
export interface UserPayslipRequestInput {
    startDate?: Date;
    endDate?: Date;
    userIds?: string[];
    payslipIds?: number[];
    status?: string;
    departmentId?: number;
    department?: number;
    employmentStatus?: string;
    month?: number;
    year?: number;
    pageSize?: number;
    pageNumber?: number;
}

// ..\mowazaf\Data\Payroll\RequestInputModels\BulkGeneratePayslipsInput.cs
export interface BulkGeneratePayslipsInput {
    userIds?: string[];
    department?: number;
    month?: number;
    year?: number;
    includeVariablePay: boolean;
    includeAdjustmentAdditions: boolean;
    includeAdjustmentDeductions: boolean;
    includeDeductions: boolean;
    includeTaxCalculations: boolean;
    generatePdf: boolean;
    sendEmailNotification: boolean;
}

// ..\mowazaf\Data\Payroll\Enities\PayslipBranding.cs
export interface PayslipBranding extends Record {
    id: number;
    companyId: number;
    company?: Company;
    logoUrl?: string;
    headerSettings?: string;
    footerSettings?: string;
    layoutSettings?: string;
    customization?: string;
}

// ..\mowazaf\Data\Payroll\ViewModels\PayslipBrandingView.cs
export interface PayslipBrandingView {
    id: number;
    companyId: number;
    enableBranding?: boolean;
    logoUrl?: string;
    logoSettings?: string;
    headerSettings?: string;
    footerSettings?: string;
    layoutSettings?: string;
    customization?: string;
    updatedOn?: Date;
}

// ..\mowazaf\Data\Payroll\InputModels\PayslipBrandingInput.cs
export interface PayslipBrandingInput {
    id?: number;
    enableBranding?: boolean;
    logoUrl?: string;
    logoSettings?: string;
    headerSettings?: string;
    footerSettings?: string;
    layoutSettings?: string;
    customization?: string;
}

// ..\mowazaf\Data\Payroll\InputModels\BulkNotifyPayslipsInput.cs
export interface BulkNotifyPayslipsInput {
    payslipIds?: number[];
    userIds?: string[];
    departmentIds?: number[];
    payrollDateFrom?: Date;
    payrollDateTo?: Date;
    notifyAll?: boolean;
}

// ── EOS Compliance types ──────────────────────────────────────────────────────

export type EosLawVersion = 'NewLaw' | 'OldLaw' | 'Custom';

export type DepartureReasonEnum =
  | 'Resignation'
  | 'TerminationWithCause'
  | 'TerminationWithoutCause'
  | 'MutualAgreement'
  | 'Redundancy'
  | 'Retirement'
  | 'DeathOrDisability'
  // FDL 33/2021 Art. 44 — for-cause sub-categories; gratuity may be withheld at employer discretion.
  | 'TerminationArt44_Impersonation'
  | 'TerminationArt44_FaultCausingLoss'
  | 'TerminationArt44_SafetyViolation'
  | 'TerminationArt44_RepeatedMisconduct'
  | 'TerminationArt44_AbsenceWithoutExcuse'
  | 'TerminationArt44_CompetitorEmployment'
  | 'TerminationArt44_TradeSecretBreach';

/** FDL 33/2021 Art. 44 — returns true for for-cause termination reasons that permit gratuity withholding. */
export const isForCauseArt44 = (reason: DepartureReasonEnum): boolean =>
  reason === 'TerminationWithCause' ||
  reason === 'TerminationArt44_Impersonation' ||
  reason === 'TerminationArt44_FaultCausingLoss' ||
  reason === 'TerminationArt44_SafetyViolation' ||
  reason === 'TerminationArt44_RepeatedMisconduct' ||
  reason === 'TerminationArt44_AbsenceWithoutExcuse' ||
  reason === 'TerminationArt44_CompetitorEmployment' ||
  reason === 'TerminationArt44_TradeSecretBreach';

/**
 * KSA Labor Law Art. 80 — returns true for the for-cause reasons that map cleanly to a named Art. 80 ground and
 * therefore permit FULL gratuity forfeiture (subject to procedural safeguards). Mirrors backend
 * EosComplianceService.IsForfeitableKsaArt80. The generic TerminationWithCause and CompetitorEmployment are
 * excluded (no Art. 80 analogue → pay full, HR judgement).
 */
export const isForfeitableKsaArt80 = (reason: DepartureReasonEnum): boolean =>
  reason === 'TerminationArt44_Impersonation' ||
  reason === 'TerminationArt44_FaultCausingLoss' ||
  reason === 'TerminationArt44_SafetyViolation' ||
  reason === 'TerminationArt44_RepeatedMisconduct' ||
  reason === 'TerminationArt44_AbsenceWithoutExcuse' ||
  reason === 'TerminationArt44_TradeSecretBreach';

/**
 * Who drove the separation, for settlement notice/deduction purposes. Mirrors backend
 * DepartureInitiator (Data/Payroll/SettlementComplianceRules.cs) + EosComplianceService.ClassifyInitiator:
 * Worker = Resignation; Neutral = mutual / life-event departures where a notice period is moot; Employer =
 * everything else (all terminations, redundancy, every Art. 44 for-cause sub-category).
 */
export type DepartureInitiator = 'Worker' | 'Employer' | 'Neutral';

/** Mirror of backend EosComplianceService.ClassifyInitiator. Accepts the raw EOS departureReason string. */
export const classifyInitiator = (reason: DepartureReasonEnum | string): DepartureInitiator => {
  if (reason === 'Resignation') return 'Worker';
  if (reason === 'MutualAgreement' || reason === 'Retirement' || reason === 'DeathOrDisability') return 'Neutral';
  return 'Employer';
};

export type SettlementStatus = 'Draft' | 'Confirmed' | 'Paid';

/** Lifecycle transition recorded on a settlement. Mirrors backend SettlementAuditAction. */
export type SettlementAuditAction = 'Confirmed' | 'Reopened' | 'Paid' | 'Reversed';

export interface EosComplianceFlag {
  label: string;
  passed: boolean;
  detail: string;
}

export interface EosCalculationStep {
  stepNumber: number;
  description: string;
  formula: string;
  result: string;
  lawCitation: string;
}

export interface EosBreakdownView {
  serviceYearsDecimal: number;
  dailyRate: number;
  band1Years: number;
  band1Amount: number;
  band2Years: number;
  band2Amount: number;
  rawGratuity: number;
  lawFactor: number;
  twoYearCap: number;
  isCapped: boolean;
  // True when no component is flagged basic and gratuity fell back to gross pay (overstates the basic-based award).
  usedGrossFallback?: boolean;
  finalGratuity: number;
  lawVersion: EosLawVersion;
  isEligible: boolean;
  eligibleFrom?: string;
  complianceFlags: EosComplianceFlag[];
  steps: EosCalculationStep[];
  // §8 — only for an hourly employee: the average monthly approved hourly earnings the base uses, and how
  // many payroll months (last 12, months worked) it covers. Absent for salaried employees.
  hourlyAverageMonthlyEarnings?: number;
  hourlyAverageMonthsWorked?: number;
}

export interface EosContextView {
  hireDate: string;
  serviceYearsDecimal: number;
  lawVersion: EosLawVersion;
  isEligible: boolean;
  eligibleFrom?: string;
  basicSalary: number;
  grossSalary: number;
  // True when the employee has salary components but none is flagged basic → basicSalary is 0.
  noBasicConfigured?: boolean;
  // CO-358 — the company pinned the EOS base mode to Basic-only and NO component is flagged basic, so
  // the base resolved to ZERO and gratuity/leave encashment compute to 0.00. Disjoint from
  // `noBasicConfigured` above and never both true: that one means the base was substituted with GROSS
  // (overstates), this one means there is no base at all (pays nothing). Unlike it, this is not
  // KSA-excluded — a KSA tenant pinned to Basic-only collapses identically.
  isSilentZeroBase?: boolean;
  leaveBalance: number;
  existingEos?: UserEndOfServiceView;
  settlementStatus?: SettlementStatus;
  // Stored (confirmed/paid) settlement breakdown, for the locked-view read-only render.
  existingSettlement?: SettlementBreakdownView;
  // Settlement payment lifecycle — populated once the settlement is marked Paid (else undefined).
  settlementPaidOn?: string;
  settlementPaymentReference?: string;
  // §8 — only for an hourly employee: the average monthly approved hourly earnings the base uses, and how
  // many payroll months (last 12, months worked) it covers. Absent for salaried employees.
  hourlyAverageMonthlyEarnings?: number;
  hourlyAverageMonthsWorked?: number;
}

export interface SettlementBreakdownView {
  gratuityAmount: number;
  leaveEncashmentAmount: number;
  leaveDaysEncashed: number;
  proratedFinalMonthAmount: number;
  compensationInLieuAmount: number;
  // Q11 (owner 2026-09-14, docs/exit-date-lifecycle-spec.md §10) — approved-but-unpaid hourly shift pay
  // for work dated AFTER a back-dated exit date, settled here because payroll generates no run for a
  // month that starts after the exit (the CO-657 skip), so the hourly spec's D6 catch-up has nowhere to
  // roll it. Optional: settlements confirmed before Q11 have a snapshot without these fields.
  approvedHourlyAfterExitAmount?: number;
  approvedHourlyAfterExitCount?: number;
  totalDeductions: number;
  deductionCapApplied: boolean;
  deductionCapWarning: number;
  finalSettlementAmount: number;
  lastWorkingDay: string;
  // KSA Art. 92 — employer-loan repayment portion of the deductions (echoed from input; null for UAE/unmodelled).
  employerLoanDeductions?: number | null;
  // CO-279 — leave encashment fell back to TOTAL GROSS because no basic wage could be established.
  // Same polarity as EosContextView.noBasicConfigured: false for KSA and for a pinned EOS base mode.
  // Optional because settlements confirmed before CO-279 have a snapshot without this field.
  noBasicConfigured?: boolean;
  // CO-358 — the settlement's base was pinned to Basic-only with nothing flagged basic, so leave
  // encashment is 0.00. Optional for the same reason as `noBasicConfigured`: snapshots confirmed before
  // CO-358 have no such field and correctly read as undefined.
  isSilentZeroBase?: boolean;
  // §8 — only for an hourly employee: the average monthly approved hourly earnings the base uses, and how
  // many payroll months (last 12, months worked) it covers. Absent for salaried employees.
  hourlyAverageMonthlyEarnings?: number;
  hourlyAverageMonthsWorked?: number;
}

export interface PreviewEosInput {
  userId: string;
  endOfServiceId: number;
  departureReason: DepartureReasonEnum;
  contractType: string;
  departureDate: string;
  /** For-cause gratuity override. Meaningful for KSA Art. 80: when true, HR waives forfeiture and pays in full. */
  allowGratuityDespiteCause?: boolean;
  /** KSA Art. 80 — worker was given a chance to respond before the for-cause dismissal (procedural safeguard). */
  art80ProcedureConfirmed?: boolean;
  /** KSA Art. 80 — a written warning is on file (procedural safeguard). */
  art80WarningOnFile?: boolean;
}

export interface PreviewSettlementInput {
  userId: string;
  noticePeriod: number;
  compensationInLieu: boolean;
  lastWorkingDay: string;
  totalDeductions: number;
  // KSA Art. 92 — employer-loan repayment portion of totalDeductions, validated against the 10% sub-cap. Optional.
  employerLoanDeductions?: number | null;
}

/** Reopen a Confirmed settlement back to Draft. Reason captured on the audit row. Mirrors backend ReopenSettlementInput. */
export interface ReopenSettlementInput {
  userId: string;
  reason?: string;
}

/** Mark a Confirmed settlement as Paid, recording the payment reference. Mirrors backend MarkSettlementPaidInput. */
export interface MarkSettlementPaidInput {
  userId: string;
  paymentReference?: string;
  reason?: string;
}

/**
 * Immutable settlement-lifecycle audit row. Mirrors backend SettlementAuditLog (Record-derived).
 * NOTE: performedById / performedOn (NOT calculatedById / calculatedOn like the EOS audit).
 */
export interface SettlementAuditLogEntry {
  id: number;
  userId: string;
  companyId: number;
  performedById: string;
  performedOn: string;
  action: SettlementAuditAction;
  amount: number;
  paymentReference?: string;
  reason?: string;
  notes?: string;
  createdOn: string;
}

export interface EosAuditLogEntry {
  id: number;
  userId: string;
  companyId: number;
  calculatedById: string;
  calculatedOn: string;
  eosTypeName: string;
  serviceYearsDecimal: number;
  dailyRate: number;
  band1Amount: number;
  band2Amount: number;
  lawFactor: number;
  capApplied: boolean;
  finalAmount: number;
  previousAmount?: number;
  notes?: string;
}

// ── WPS / SIF Compliance ──

export interface WpsDeadlineView {
  year: number;
  month: number;
  payrollProcessedDate?: string;
  daysElapsed: number;
  daysRemaining: number;
  deadlineDays: number;
  zone: 'Green' | 'Amber' | 'Red' | 'Overdue';
  isSubmitted: boolean;
  submittedOn?: string;
  currentTierCode?: 'PreReminder' | 'Day0' | 'Day10' | 'Day15' | 'Day20' | 'Day30' | 'Stale' | 'BeforeWindow';
  isRunPayrollMode?: boolean;
}

export interface SifPreflightView {
  companyPassed: boolean;
  companyChecks: SifCompanyCheck[];
  employeesPassed: number;
  employeesTotal: number;
  employeeIssues: SifEmployeeIssue[];
  canGenerate: boolean;
}

export interface SifCompanyCheck {
  code: string;
  label: string;
  passed: boolean;
  detail?: string;
  hint?: string;
}

/**
 * Maps a SIF company-check `code` to the settings route that resolves it.
 * Route ownership lives on the client (the backend emits only the stable code),
 * so renaming a route is a single-file change here rather than a hunt through C#.
 */
export const sifCheckFixPath = (code: string): string | undefined => {
  switch (code) {
    case 'CurrencyAED':
    case 'CurrencySAR':
      return '/settings/currency-and-banks';
    // Employer identifiers (MOL, CR, Mudad, Qiwa) and the WPS bank routing code
    // live on the company profile under Settings → General → Details.
    case 'MOLNumber':
    case 'BankRoutingCode':
    case 'CommercialRegistration':
    case 'MudadEstablishment':
    case 'QiwaEntityId':
      return '/settings/general-details';
    // GOSI rate schedule and the Mudad allowance mapping live in Compliance Config.
    case 'GosiRatesAvailable':
    case 'AllowanceMudadCategory':
      return '/settings/payroll/compliance';
    default:
      return undefined;
  }
};

export interface SifEmployeeIssue {
  userId: string;
  employeeName: string;
  issue: string;
  field: string;
  /** Soft issues (e.g. a CCHI coverage gap) surface but do not block file generation. */
  isWarning?: boolean;
}

/**
 * Maps a per-employee SIF/Mudad pre-flight issue `code` to the profile route + section
 * anchor where HR resolves it. Mirrors {@link sifCheckFixPath} but for employee-level
 * rows. The matching `#hash` targets live on the employee Personal page, which scrolls
 * the section into view on load.
 */
export const sifEmployeeIssueFixPath = (issue: string, userId: string): string | undefined => {
  if (!userId) return undefined;
  const base = `/profile/${encodeURIComponent(userId)}/personal`;
  switch (issue) {
    case 'ibanMissing':
    case 'ibanFormatInvalid':
    case 'ibanChecksumInvalid':
      return `${base}#iban`;
    case 'identifierMissing':
    case 'identifierBoth':
    case 'iqamaExpired':
    case 'qiwaContractMissing':
      return `${base}#ksa-identity`;
    case 'gosiSubscriptionMissing':
      return `${base}#gosi`;
    case 'cchiMissing':
    case 'cchiExpired':
      return `/profile/${encodeURIComponent(userId)}/payroll/benefits#cchi`;
    default:
      return base;
  }
};

export interface WpsSifSubmissionView {
  id: number;
  year: number;
  month: number;
  generatedOn: string;
  generatedByName: string;
  employeeCount: number;
  totalAmount: number;
  status: string;
  submittedOn?: string;
  notes?: string;
}

export type MudadAllowanceCategory = 'Basic' | 'Housing' | 'Transport' | 'Other';

export interface KsaWpsSubmissionView {
  id: number;
  year: number;
  month: number;
  generatedOn: string;
  generatedByName: string;
  employeeCount: number;
  totalAmount: number;
  fileHash: string;
  status: 'Generated' | 'Uploaded';
  uploadedOn?: string | null;
  uploadedByName?: string | null;
  mudadReference?: string | null;
  note?: string | null;
}

export interface KsaMudadMarkUploadedInput {
  mudadReference: string;
  note?: string;
}

// ── GPSSA / ADPF Pension ──

export interface PensionCoverageView {
  userId: string;
  employeeName: string;
  nationality?: string;
  emirate?: string;
  scheme?: string;
  employeeRate: number;
  employerRate: number;
  basicSalary: number;
  employeeDeduction: number;
  employerContribution: number;
  isRegistered: boolean;
  status: string;
}

export interface GpssaRemittanceView {
  year: number;
  month: number;
  gpssaTotalEmployee: number;
  gpssaTotalEmployer: number;
  gpssaTotal: number;
  gpssaCount: number;
  adpfTotalEmployee: number;
  adpfTotalEmployer: number;
  adpfTotal: number;
  adpfCount: number;
  grandTotal: number;
  contributions: MonthlyPensionContributionView[];
}

export interface MonthlyPensionContributionView {
  id: number;
  userId: string;
  employeeName: string;
  year: number;
  month: number;
  scheme: string;
  basicSalary: number;
  employeeContribution: number;
  employerContribution: number;
  totalContribution: number;
  isIncludedInPayroll: boolean;
  isPending: boolean;
}

export interface UserPensionRecordView {
  id: number;
  userId: string;
  employeeName: string;
  scheme: string;
  rateBracket: string;
  registrationDate: string;
  employeeContributionRate: number;
  employerContributionRate: number;
  isActive: boolean;
}

export interface UserPensionRecordInput {
  id?: number;
  userId: string;
  scheme: string;
  rateBracket: string;
  registrationDate: string;
  employeeContributionRate?: number;
  employerContributionRate?: number;
  isActive: boolean;
}

export interface GpssaComplianceSummaryView {
  totalUaeNationals: number;
  coveredCount: number;
  gapCount: number;
  fullyCovered: boolean;
}

export interface GpssaContributionSkipView {
  userId: string;
  employeeName: string;
  scheme: string;
  reason: string;
  reasonCode: string;
}

export interface GpssaGenerationResultView {
  contributions: MonthlyPensionContributionView[];
  skipped: GpssaContributionSkipView[];
  enrolledCount: number;
  regenerated: boolean;
  lockedByPayroll: boolean;
}

export type GpssaAutoMode = 'Off' | 'AutoPreview' | 'AutoCommit';

export interface GpssaAutoGenerationSettingsView {
  mode: GpssaAutoMode;
  dayOfMonth: number;
  lastRunAt?: string | null;
  pendingCount: number;
}

export interface GpssaAutoGenerationInput {
  mode: GpssaAutoMode;
  dayOfMonth: number;
}

export interface NationalityCountView {
  nationality: string;
  count: number;
}

export interface EmiratizationSummaryView {
  totalWorkforce: number;
  uaeNationalCount: number;
  currentRate: number;
  targetRate: number;
  targetUaeHires: number;
  gap: number;
  quotaApplies: boolean;
  legalReference: string;
  nationalityBreakdown: NationalityCountView[];
}

// ── Compliance Dashboard ──

export interface ComplianceDashboardView {
  wpsStatus: WpsDeadlineView;
  deductionCapBreachCount: number;
  firstBreach?: DeductionCapBreachView;
  gpssaCovered: number;
  gpssaTotal: number;
  gpssaFullyCovered: boolean;
  sifValidEmployees: number;
  sifTotalEmployees: number;
  probationWarningCount: number;
  basicSalaryConfigCount: number;
  basicSalaryInvariantHealthy: boolean;
  recentEvents: ComplianceEventView[];
}

export interface DeductionCapBreachView {
  userId: string;
  employeeName: string;
  grossSalary: number;
  /** Deductions that count toward the 50% cap (Standard, Disciplinary, Loan, Other). */
  totalDeductions: number;
  /** FDL 33/2021 Art. 25 exception — court-ordered deductions, reported separately from the cap. */
  courtOrderedDeductions: number;
  cap: number;
  breachAmount: number;
}

export interface ProbationWarningView {
  userId: string;
  employeeName: string;
  hireDate: string;
  /** Configured probation end date, if HR set one. */
  probationEndDate?: string;
  daysOnProbation: number;
  daysOverLimit: number;
  /** Statutory cap in days from hire (183 UAE / 180 KSA). */
  maxAllowedDays: number;
  /** "ExceedsLegalMax" | "Overrun". */
  reason: string;
  /** Legal citation, e.g. "FDL 33/2021 Art. 9" / "KSA Labor Law Art. 53-54". */
  legalReference: string;
  /** "UAE" | "KSA". */
  country: string;
}

export interface ComplianceEventView {
  id: number;
  eventType: string;
  severity: string;
  title: string;
  detail: string;
  userId?: string;
  isResolved: boolean;
  resolvedOn?: string;
  createdOn: string;
}

// ──────────────────────────────────────────────────────────────────────────
// GOSI ANI (Saudi pension) — Gap 5
// ──────────────────────────────────────────────────────────────────────────

export type GosiScheme = 'Legacy' | 'NewPhased';
export type GosiBranchType = 'ANI' | 'OH' | 'SANED';

export interface EmployeeGosiRecordView {
  id: number;
  userId: string;
  employeeName: string;
  gosiSubscriptionDate: string | null;
  isLegacyScheme: boolean;
}

export interface EmployeeGosiRecordInput {
  userId: string;
  gosiSubscriptionDate: string | null;
  isLegacyScheme: boolean;
}

export interface GosiRateScheduleView {
  id: number;
  year: number;
  scheme: GosiScheme;
  branchType: GosiBranchType;
  employerRate: number;
  employeeRate: number;
  wageCap: number;
  effectiveFrom: string;
  effectiveTo: string | null;
  isArchived: boolean;
}

export interface GosiRateScheduleInput {
  id?: number;
  year: number;
  scheme: GosiScheme;
  branchType: GosiBranchType;
  employerRate: number;
  employeeRate: number;
  wageCap: number;
  effectiveFrom: string;
  effectiveTo: string | null;
}

export interface GosiCoverageRow {
  userId: string;
  employeeName: string;
  nationality: string | null;
  basic: number;
  housing: number;
  cappedBase: number;
  employerRate: number;
  employeeRate: number | null;        // null for OH
  employerAmount: number;
  employeeAmount: number | null;      // null for OH
  rateScheduleId: number;
}

export interface GosiBranchSummary {
  branchType: 'ANI' | 'OH' | 'SANED';
  employerTotal: number;
  employeeTotal: number | null;       // null means employer-only branch (OH)
  coveredEmployeesCount: number;
  coverage: GosiCoverageRow[];
}

export interface KsaGosiPanelData {
  year: number;
  month: number;
  saudiNationalsCount: number;
  totalEmployeesCount: number;
  totalEmployerObligation: number;
  aniSummary: GosiBranchSummary;
  ohSummary: GosiBranchSummary;
  sanedSummary: GosiBranchSummary;
  activeRates: GosiRateScheduleView[];
  /** CO-256 — statutory-base guard for this company. Never null from the server. */
  statutoryBaseHealth: StatutoryBaseHealthView;
}

/**
 * CO-256 — how many employees contribute on a ZERO statutory basic wage, and how much
 * statutory base that silently removes. `underContributionPercent` is 0-100.
 */
export interface StatutoryBaseHealthView {
  /** 'Auto' | 'BasicOnly' | 'Gross' */
  baseMode: string;
  /** True when the configured mode counts ONLY components flagged as the basic wage. */
  baseModeConsumesFlag: boolean;
  basicSalaryConfigCount: number;
  /** EVERY non-archived employee, including those with no pay components at all. */
  evaluatedEmployeeCount: number;
  /**
   * CO-256 (Finding 1) — employees who have any pay data (inferred base > 0). This is the
   * population `affectedEmployeeCount` is drawn from, and therefore the ONLY honest denominator
   * for it; `evaluatedEmployeeCount` includes employees with no salary configured and understates
   * severity by an arbitrary factor.
   */
  employeesWithPayDataCount: number;
  affectedEmployeeCount: number;
  reportedWageBaseTotal: number;
  expectedWageBaseTotal: number;
  underContributionBase: number;
  underContributionPercent: number;
  isHealthy: boolean;
}

// ──────────────────────────────────────────────────────────────────────────
// CCHI (Council of Health Insurance) — Gap 18
// ──────────────────────────────────────────────────────────────────────────

export type CchiPlanTier = 'ClassA' | 'ClassB' | 'ClassC' | 'Vip';

export type CchiVisaCategory =
  | 'SeniorExecutive'
  | 'Professional'
  | 'Skilled'
  | 'SemiSkilled'
  | 'Domestic';

export interface CchiTierWarning {
  expectedTier: string;
  actualTier: string;
}

export interface EmployeeHealthInsuranceView {
  id: number;
  userId: string;
  employeeName?: string | null;
  providerName?: string | null;
  policyNumber?: string | null;
  planTier?: string | null;
  effectiveDate?: string | null;
  expiryDate?: string | null;
  monthlyPremium?: number | null;
  employeePremiumShare?: number | null;
  policyDocumentUrl?: string | null;
  waiverReason?: string | null;
  isWaived: boolean;
  tierWarning?: CchiTierWarning | null;
  /**
   * CO-269 — no tier-schedule band covers this employee, so the expected tier could not be
   * computed. UNKNOWN, not non-compliant: render neutrally, never as an error.
   */
  expectedTierUnknown?: boolean;
}

export interface EmployeeHealthInsuranceInput {
  userId: string;
  providerName?: string | null;
  policyNumber?: string | null;
  planTier?: CchiPlanTier | null;
  effectiveDate?: string | null;
  expiryDate?: string | null;
  monthlyPremium?: number | null;
  employeePremiumShare?: number | null;
  policyDocumentUrl?: string | null;
  waiverReason?: string | null;
}

export interface CchiTierScheduleView {
  id: number;
  visaCategory: string;
  minMonthlySalary: number;
  maxMonthlySalary?: number | null;
  expectedTier: string;
  effectiveFrom: string;
  effectiveTo?: string | null;
  isArchived: boolean;
}

export interface CchiTierScheduleInput {
  id?: number | null;
  visaCategory: CchiVisaCategory;
  minMonthlySalary: number;
  maxMonthlySalary?: number | null;
  expectedTier: CchiPlanTier;
  effectiveFrom: string;
  effectiveTo?: string | null;
}

export interface CchiCoverageRow {
  userId: string;
  employeeName: string;
  visaCategory?: string | null;
  status: 'active' | 'expiringIn7' | 'expiringIn30' | 'expiringIn60' | 'expired' | 'waived' | 'missing';
  expiryDate?: string | null;
  planTier?: string | null;
  expectedTier?: string | null;
  /** CO-269 — expected tier could not be computed for this row (unknown, not non-compliant). */
  expectedTierUnknown?: boolean;
  policyId?: number | null;
  policyDocumentUrl?: string | null;
}

export interface CchiPanelData {
  totalNonSaudisCount: number;
  activeCount: number;
  expiringIn7Count: number;
  expiringIn30Count: number;
  expiringIn60Count: number;
  expiredCount: number;
  waivedCount: number;
  missingCount: number;
  coverage: CchiCoverageRow[];
}
